import React, { useState, useEffect, useRef } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import axios from 'axios'

import Header from '../header/Header'
import LoadingSpinner from '../common/LoadingSpinner'
import ProductCard from '../catalogue/nodes/ProductCard'
import FolderProduct from '../catalogue/nodes/FolderProduct'
import { Breadcrumb, HeaderBreadcrumb, BreadcrumbPortal } from '../common/Breadcrumb'
import { BusinessUnitMultiSelect } from '../fields'
import FinalProductBrowseV2 from './FinalProductBrowseV2'
import { sinAcentos, segmentoNombre } from './catalogoTexto'
import { articleSearchApi } from '../../api/articleSearchApi'
import '../../assests/styles/catalogue/CatalogueCards.scss'
import '../../assests/styles/catalogue/NodesV2.scss'
import '../../assests/styles/catalogue/catalogueButtons.scss'
import '../../assests/styles/catalogue/ProductCard.scss'

/**
 * Nivel N del Catálogo: el contenido de una família o subfamília.
 *
 * Sustituye a ProductsV2 (dentro de NodesV2.js), que se queda intacto.
 * Se declara a sí mismo como ruta hija, igual que hace el catálogo antiguo,
 * porque el árbol se anida sin límite: /product/44/X/product/55/Y/...
 *
 * La multicategoría NO interviene aquí: los hijos salen de id_ArticuloPadre,
 * que sigue siendo un solo padre.
 *
 * Visibilidad: réplica exacta de la actual. Navegar no restringe por las
 * unidades de negocio del usuario, sólo por el filtro que se elige en pantalla.
 */

const TAM_PAGINA = 60

const ProductBrowseV2 = ({ match }) => {
  const idPadre = match.params.idProduct
  const nombrePadre = decodeURIComponent(match.params.nombre || '')
  const history = useHistory()
  const base = match.url

  const [unidades, setUnidades] = useState([])
  const [texto, setTexto] = useState('')
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)

  const peticionRef = useRef(0)
  const unidadesTexto = unidades.join(',')

  useEffect(() => {
    const fuente = axios.CancelToken.source()
    const idPeticion = peticionRef.current + 1
    peticionRef.current = idPeticion

    setCargando(true)
    setError(null)

    articleSearchApi
      .children(
        idPadre,
        { page: 1, pageSize: TAM_PAGINA, businessUnits: unidadesTexto },
        fuente.token,
      )
      .then((res) => {
        if (idPeticion !== peticionRef.current) return
        setItems(res.data.items || [])
        setTotal(res.data.total)
        setCargando(false)
      })
      .catch((err) => {
        if (idPeticion !== peticionRef.current) return
        if (!axios.isCancel(err)) {
          const codigo = err && err.response ? err.response.status : '—'
          setError(`No se ha podido cargar (HTTP ${codigo}).`)
        }
        setCargando(false)
      })

    return () => {
      peticionRef.current += 1
      fuente.cancel()
    }
  }, [idPadre, unidadesTexto])

  // Filtrado local sobre lo ya cargado, igual que hace hoy esta pantalla.
  const visibles = texto.trim()
    ? items.filter((i) => sinAcentos(i.nombre).includes(sinAcentos(texto)))
    : items

  const carpetas = visibles.filter((i) => !i.isArticuloFinal)
  const finales = visibles.filter((i) => i.isArticuloFinal)

  const recuento = [
    `${total} elementos`,
    texto.trim() && visibles.length !== total ? `${visibles.length} filtrados` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <Breadcrumb to={base}>{nombrePadre}</Breadcrumb>

      <Switch>
        {/* Los mismos patrones que declara el catálogo antiguo, para que la
            navegación hacia abajo sea idéntica. Se declaran aquí, y no en
            CatalogueSearchHost, porque el árbol se anida sin límite. */}
        <Route
          path={`${match.url}/product/:idProduct/:nombre`}
          component={ProductBrowseV2}
        />
        <Route
          path={`${match.url}/final/:idProducto`}
          component={FinalProductBrowseV2}
        />

        <Route>
          <div className='products'>
            <Header text={'Catálogo'} route={() => history.goBack()} backBtn={true} />

            <div className='container-fluid m-0 p-0'>
              <div className='row'>
                <div className='col-sm-12 col-xl-8 justify-content-start d-flex'>
                  <HeaderBreadcrumb>
                    <div
                      style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}
                    >
                      <BreadcrumbPortal />
                      {!error ? (
                        <span style={{ whiteSpace: 'nowrap', fontSize: 14, opacity: 0.75 }}>
                          · {recuento}
                        </span>
                      ) : null}
                    </div>
                  </HeaderBreadcrumb>
                </div>

                <div className='col-sm-12 col-xl-4 justify-content-end d-flex'>
                  <div className='w-100 d-flex'>
                    <div style={{ marginRight: '1rem' }}>
                      <BusinessUnitMultiSelect
                        size='xl'
                        value={unidades}
                        onChange={setUnidades}
                        placeholder='Selecciona las unidades de negocio...'
                        compact
                      />
                    </div>

                    <div className='search' style={{ minWidth: '120px' }}>
                      <div className='searchbar__wrap'>
                        <h4 className='filter__title'>Buscador</h4>
                        <input
                          className='btn-filter search placeholder-text'
                          type='text'
                          value={texto}
                          placeholder='Buscar productos...'
                          onChange={(e) => setTexto(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {error ? (
                <div
                  style={{
                    margin: '10px 0',
                    padding: '8px 12px',
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: '#a32218',
                    background: '#f6e3e1',
                    borderLeft: '3px solid #a32218',
                  }}
                >
                  {error}
                </div>
              ) : null}

              <div className='row'>
                <div className='col-12'>
                  {cargando && items.length === 0 ? (
                    <LoadingSpinner message='Cargando...' size='large' variant='primary' />
                  ) : visibles.length > 0 ? (
                    <div className='catalogue-grid'>
                      {carpetas.map((item) => (
                        <FolderProduct
                          key={`hijo-folder-${item.id_Articulo}`}
                          item={item}
                          pathname={`${base}/product/${item.id_Articulo}/${segmentoNombre(
                            item.nombre,
                          )}`}
                        />
                      ))}
                      {finales.map((item) => (
                        <ProductCard
                          key={`hijo-final-${item.id_Articulo}`}
                          item={item}
                          pathname={`${base}/final/${item.id_Articulo}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className='catalogue-no-results'>
                      <div className='no-results-content'>
                        <div className='no-results-icon'>🔍</div>
                        <div className='no-results-text'>
                          No hay productos disponibles
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Route>
      </Switch>
    </>
  )
}

export default ProductBrowseV2
